'use client';

import { useEffect, useState } from 'react';
import { useRouters } from '@/utils/app-router-migration/client';
import { Button as AltoButton } from '@alto-avios/alto-ui';
import { isAviosOpco } from '@/utils/opco-utils';
import { useClientSideOpcoContext } from '@/context/client-side-opco/opco-context';
import { AVIOS_PREFIX } from '@/constants';
import type { ShopNowButtonProperties } from './shop-now-button.types';
import usePartnerClick from '../../hooks/use-partner-click';
import trackEvent from '../../utils/track-event';
import {
  EVENT_NAME,
  USER_ACTION,
} from '../../utils/track-event/track-event.types';
import dataDogRum from '../../utils/data-dog-rum';
import { DD_ACTION } from '../../utils/data-dog-rum/data-dog-rum.types';
import { DATA_IDS } from '../../types';

const ShopNowButton = ({
  slug,
  isUserBlocked,
  merchantId,
  collinsonId = '',
  ctaLabel,
  ctaLabelTrack,
  collectionMethodType,
  setErrorCallback,
}: ShopNowButtonProperties) => {
  const { asPath } = useRouters();
  const [isTracked, setIsTracked] = useState<boolean>(false);
  const { data, isLoading, error, handleClick } = usePartnerClick(
    merchantId,
    collinsonId,
    slug,
  );

  const { opcoId } = useClientSideOpcoContext();
  const isAvios = isAviosOpco(opcoId);

  useEffect(() => {
    if (data?.tracking_url) {
      setIsTracked(true);
    }
  }, [data]);

  useEffect(() => {
    setIsTracked(false);
  }, [slug]);

  useEffect(() => {
    if (setErrorCallback) setErrorCallback(!!error);
  }, [setErrorCallback, error]);

  const ctaHandler = async () => {
    if (isUserBlocked) return;
    if (collinsonId) {
      await handleClick();
      trackEvent({
        event: EVENT_NAME.ONLINE_SHOP_NOW,
        retailerSlug: slug,
      });
      dataDogRum.addAction(DD_ACTION.SHOP_NOW, {
        slug,
        type: collectionMethodType,
      });
      return;
    }
    trackEvent({
      event: EVENT_NAME.ONLINE_LOGINBUTTON,
      user_action: USER_ACTION.RETAILER_LOG_IN,
    });
  };

  const label = isTracked ? ctaLabelTrack : ctaLabel;

  if (collinsonId) {
    return (
      <AltoButton
        as="a"
        onPress={() => ctaHandler()}
        isLoading={isLoading}
        data-id={DATA_IDS.SHOP_NOW}
        isDisabled={isUserBlocked}
        iconEndProps={{
          iconName: 'arrow-up-right-from-square',
        }}
        fullWidth
      >
        {label}
      </AltoButton>
    );
  }

  const shopNowLoginUrl = isAvios
    ? `${AVIOS_PREFIX}/api/auth/login?returnTo=${encodeURIComponent(asPath)}`
    : `/api/auth/login?returnTo=${encodeURIComponent(asPath)}`;

  return (
    <AltoButton
      as="a"
      href={shopNowLoginUrl}
      onPress={() => ctaHandler()}
      isLoading={isLoading}
      data-id={DATA_IDS.SHOP_NOW}
      fullWidth
    >
      {label}
    </AltoButton>
  );
};

export default ShopNowButton;
